import { Validator } from 'fluentvalidation-ts';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { map } from 'lodash';
import React, { useContext } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { AssertUserHasPermission } from '../../../authorisation/AssertUserHasPermission';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { parseNumberOrNull } from '../../../helpers/stringHelpers';
import { PrimaryProgressButton, SecondaryButton } from '../../../shared/Button';
import { Form, FormErrorBox } from '../../../shared/form/Form';
import { SelectOptions } from '../../../shared/form/inputs/Select';
import { SelectField } from '../../../shared/form/inputs/SelectField';
import { TextAreaField } from '../../../shared/form/inputs/TextAreaField';
import { TextInputField } from '../../../shared/form/inputs/TextInputField';
import { Modal, ModalActionButtons, ModalHeader } from '../../../shared/Modal';
import { beNumeric, haveDecimalPlaces } from '../../../validators/stringValidators';
import { allBuildTypes, BuildTypeCode, buildTypeDisplayNames } from '../project';
import { ProjectContext } from '../project-section/ProjectContext';

type Props = {
  isOpen: boolean;
  closeModal: () => void;
};

export const EditProjectModal = ({ isOpen, closeModal }: Props) => {
  const { project, setProject } = useContext(ProjectContext);

  const { error, sendRequest } = useApiRequest<UpdateProjectResponse, UpdateProjectRequestBody>({
    httpMethod: 'put',
    endpointUrl: `projects/${project && project.projectId}`,
  });

  if (project == null) {
    return null;
  }

  const onSubmit = (values: EditProjectFormModel, formikHelpers: FormikHelpers<EditProjectFormModel>) => {
    sendRequest({
      requestBody: {
        projectId: project.projectId,
        siteAddress: values.siteAddress,
        buildTypeCode: values.buildType,
        externalAirTemperatureInDegreesCelsius: assertNotNull(
          parseNumberOrNull(values.externalAirTemperatureInDegreesCelsius),
        ),
      },
      onSuccess: response => {
        formikHelpers.setSubmitting(false);

        setProject({
          ...project,
          siteAddress: response.siteAddress,
          buildTypeCode: response.buildTypeCode,
          externalAirTemperatureInDegreesCelsius: response.externalAirTemperatureInDegreesCelsius,
        });

        closeModal();
      },
      onFailure: () => {
        formikHelpers.setSubmitting(false);
      },
    });
  };

  const initialValues = {
    siteAddress: project.siteAddress,
    buildType: project.buildTypeCode,
    externalAirTemperatureInDegreesCelsius: project.externalAirTemperatureInDegreesCelsius.toString(),
  };

  return (
    <Modal isOpen={isOpen}>
      <ModalHeader>Edit Project</ModalHeader>
      <AssertUserHasPermission requiredPermission="Edit">
        <Formik onSubmit={onSubmit} initialValues={initialValues} validate={editProjectFormValidator.validate}>
          {({ isSubmitting }: FormikProps<EditProjectFormModel>) => {
            return (
              <Form>
                <TextAreaField name="siteAddress" label="Address" />
                <SelectField name="buildType" label="Build Type" options={buildTypeOptions} />
                <TextInputField name="externalAirTemperatureInDegreesCelsius" label="External Air Temperature (°C)" />
                {error && <FormErrorBox error={error} />}
                <ModalActionButtons $align="right">
                  <SecondaryButton onClick={closeModal}>Cancel</SecondaryButton>
                  <PrimaryProgressButton type="submit" inProgress={isSubmitting}>
                    Save
                  </PrimaryProgressButton>
                </ModalActionButtons>
              </Form>
            );
          }}
        </Formik>
      </AssertUserHasPermission>
    </Modal>
  );
};

type EditProjectFormModel = {
  siteAddress: string;
  buildType: BuildTypeCode;
  externalAirTemperatureInDegreesCelsius: string;
};

class EditProjectFormValidator extends Validator<EditProjectFormModel> {
  constructor() {
    super();

    this.ruleFor('siteAddress')
      .notEmpty()
      .withMessage('Please specify a Site Address')
      .maxLength(1000)
      .withMessage('Please enter no more than 1,000 characters');

    this.ruleFor('externalAirTemperatureInDegreesCelsius')
      .notEmpty()
      .must(beNumeric)
      .must(haveDecimalPlaces(1));
  }
}
const editProjectFormValidator = new EditProjectFormValidator();

const buildTypeOptions: SelectOptions<BuildTypeCode> = map(allBuildTypes, buildType => ({
  value: buildType,
  text: buildTypeDisplayNames[buildType],
}));

export type UpdateProjectResponse = {
  projectId: number;
  siteAddress: string;
  buildTypeCode: BuildTypeCode;
  externalAirTemperatureInDegreesCelsius: number;
};

export type UpdateProjectRequestBody = {
  projectId: number;
  siteAddress: string;
  buildTypeCode: BuildTypeCode;
  externalAirTemperatureInDegreesCelsius: number;
};
